import React, { useEffect, useState } from 'react'
import styles from './dropdown.module.scss'
import Select, { components } from 'react-select'

interface IOption {
  label: string,
  value: string | number
  discount?: string
}

interface IProps {
  label?: string
  modifiers?: string
  curried?: boolean
  readOnly?: boolean
  value?: any
  maxHeight?: number
  sortOptions?: boolean
  handleOnChange?: any
  isSearchable?: boolean
  clearInputValue?: boolean
  placeholder?: string,
  options: Array<IOption>
}

const DropDown = React.memo(function DropDown({ options = [], value, sortOptions = true, ...props }: IProps) {
  const { Option } = components;
  const [selected, setSelected] = useState<IOption>(null)

  useEffect(() => {
    if (value) {
      setSelected(options.find(option => option.value == value))
    } else if (value === '') {
      setSelected(null)
    }
  }, [value, options])

  const IconOption = (props: any) => {
    return (
      <Option {...props} className='tw-flex'>
        {props.data.label}
        {props.isSelected && <svg className='tw-w-5 tw-h-5 tw-ml-auto' xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="#2bab66">
          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
        </svg>}
      </Option>
    )
  };

  const DropdownIndicator = props => {
    return (
      components.DropdownIndicator && (
        <components.DropdownIndicator {...props}>
          <svg xmlns="http://www.w3.org/2000/svg" className="tw-h-6 tw-w-6" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </components.DropdownIndicator>
      )
    );
  };

  const handleOnChange = (option: IOption) => {
    if (props.curried) {
      props.handleOnChange()(option.value)
    } else {
      props.handleOnChange(option.value, option.discount)
    }
    setSelected(option)
  }

  const selectStyles = {
    control: (base: any) => ({
      ...base,
      "&": {
        borderRadius: '8px',
        height: '40px',
        boxShadow: '0 1.5px 3px 0 rgba(0, 0, 0, 0.08)',
        border: 'solid 0.5px #cacfd7'
      },
      '&:hover': {
        boxShadow: '0 0 0 3px #e9ecf0',
        border: 'solid 1px #cacfd7'
      },
    }),
    valueContainer: (base: any) => {
      return {
        ...base,
        '&': {
          paddingLeft: '14px'
        }
      }
    },
    menu: (base: any) => {
      return {
        ...base,
        '&': {
          zIndex: 20,
          borderRadius: '8px'

        }
      }
    },
    menuList: (base: any) => {
      return {
        ...base,
        '&': {
          padding: '0',
          maxHeight: props.maxHeight ? props.maxHeight : 400,
          overflow: 'auto',
          boxShadow: '0 2px 3px 0 rgba(0, 0, 0, 0.02), 0 4px 7px 0 rgba(0, 0, 0, 0.03), 0 9px 14px 0 rgba(0, 0, 0, 0.04), 0 18px 29px 0 rgba(0, 0, 0, 0.05), 0 48px 80px 0 rgba(0, 0, 0, 0.07)',
          borderRadius: '8px'
        }
      }
    },
    indicatorsContainer: (base) => {
      return {
        ...base,
        '&': {
          borderLeft: '1px solid #e2e2e2;'
        },
      }
    },
    singleValue: (base: any) => {
      return {
        ...base,
        '&': {
          fontSize: '14px'
        },
      }
    },
    placeholder: (base: any) => {
      return {
        ...base,
        '&': {
          fontSize: '14px'
        },
      }
    },

    option: (base: any, state: any) => {
      return {
        ...base,
        '&': {
          padding: '12px 16px',
          fontSize: '14px',
          opacity: state.isDisabled ? .5 : 1,
          fontWeight: state.isSelected ? '500' : '400',
          background: state.isDisabled ? '#ddd' : state.isSelected ? '#f9f9fb' : '#fff',
          color: '#2d2c2b',
          borderBottom: '1px solid #e2e2e2'
        },
        '&:hover': {
          fontWeight: state.isDisabled ? '400' : '500',
          cursor: state.isDisabled ? 'not-allowed' : 'default',
          background: state.isDisabled ? '#ddd' : '#f9f9fb'
        }
      }
    }
  };

  return (
    <div className={`${styles.inputField} ${props.modifiers ? props.modifiers : ''}`}>
      {props.label && <span className={styles.inputLabel}>{props.label}</span>}
      <Select
        placeholder={props.placeholder}
        options={sortOptions ? options.sort(function (a, b) {
          if (a.label < b.label) { return -1; }
          if (a.label > b.label) { return 1; }
          return 0;
        }) : options}
        value={selected}
        isDisabled={props.readOnly}
        styles={selectStyles}
        isSearchable={props.isSearchable}
        className={styles.dropDown}
        components={{ Option: IconOption, IndicatorSeparator: () => null, DropdownIndicator }}
        classNamePrefix="react-select"
        onChange={(val) => handleOnChange(val)}
      />
    </div>
  )
})

export default DropDown
